import type { Database } from '@/types/database'

type Igreja = Database['public']['Tables']['igrejas']['Row']

export interface PWAManifest {
  name: string
  short_name: string
  description: string
  start_url: string
  display: string
  background_color: string
  theme_color: string
  orientation: string
  icons: Array<{
    src: string
    sizes: string
    type: string
    purpose?: string
  }>
  categories: string[]
  lang: string
  scope: string
}

export interface OpenGraphData {
  title: string
  description: string
  image: string
  url: string
  type: string
  siteName: string
  locale: string
}

/**
 * Gera dados Open Graph dinamicamente com base nos dados da igreja
 */
export function generateOpenGraphData(
  igreja?: Igreja | null, 
  pageData?: {
    title?: string
    description?: string
    image?: string
    type?: string
  }
): OpenGraphData {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const currentUrl = typeof window !== 'undefined' ? window.location.href : baseUrl
  
  // Valores padrão mais genéricos
  const defaultTitle = 'Igreja Digital'
  const defaultDescription = 'Plataforma de gestão para igrejas'
  const defaultImage = `${baseUrl}/pwa-512x512.svg`
  
  // Usar dados da igreja se disponíveis
  const siteName = igreja?.nome || defaultTitle
  const title = pageData?.title ? `${pageData.title} - ${siteName}` : siteName
  const description = pageData?.description || igreja?.descricao || defaultDescription
  
  // Usar logo da igreja se disponível
  let image = defaultImage
  if (igreja?.id) {
    image = `${baseUrl}/512X512 - ${igreja.id}.png`
  }
  if (pageData?.image) {
    image = pageData.image.startsWith('http') ? pageData.image : `${baseUrl}${pageData.image}`
  }

  console.log('🏛️ [generateOpenGraphData] Dados gerados:', {
    igreja: igreja ? {
      nome: igreja.nome,
      descricao: igreja.descricao,
      id: igreja.id
    } : null,
    pageData,
    result: {
      title,
      description,
      image,
      siteName
    }
  })
  
  return {
    title,
    description,
    image,
    url: currentUrl,
    type: pageData?.type || 'website',
    siteName,
    locale: 'pt_BR'
  }
}

/**
 * Gera meta tags Open Graph como string HTML
 */
export function generateOpenGraphMetaTags(
  igreja?: Igreja | null,
  pageData?: {
    title?: string
    description?: string
    image?: string
    type?: string
  }
): string {
  const ogData = generateOpenGraphData(igreja, pageData)
  
  return `
    <meta property="og:title" content="${ogData.title}">
    <meta property="og:description" content="${ogData.description}">
    <meta property="og:image" content="${ogData.image}">
    <meta property="og:url" content="${ogData.url}">
    <meta property="og:type" content="${ogData.type}">
    <meta property="og:site_name" content="${ogData.siteName}">
    <meta property="og:locale" content="${ogData.locale}">
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:title" content="${ogData.title}">
    <meta name="twitter:description" content="${ogData.description}">
    <meta name="twitter:image" content="${ogData.image}">
  `.trim()
}

/**
 * Gera o manifesto PWA dinamicamente com base nos dados da igreja
 */
export function generatePWAManifest(igreja?: Igreja | null): PWAManifest {
  // Valores padrão caso não haja dados da igreja
  const defaultName = 'Igreja Digital'
  const defaultDescription = 'Plataforma de gestão para igrejas'
  const defaultThemeColor = '#4F46E5'
  const defaultBackgroundColor = '#ffffff'

  // Obter a URL base atual
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  // Usar dados da igreja se disponíveis
  const name = igreja?.nome || defaultName
  const shortName = igreja?.nome ? 
    (igreja.nome.length > 12 ? 
      igreja.nome.split(' ').map(word => word[0]).join('').substring(0, 12) : 
      igreja.nome
    ) : defaultName
  
  const description = igreja?.descricao || defaultDescription
  const themeColor = igreja?.cor_primaria || defaultThemeColor
  const backgroundColor = defaultBackgroundColor

  let icons: Array<{
    src: string
    sizes: string
    type: string
    purpose?: string
  }> = []

  // Usar APENAS ícones locais baseados no ID da igreja
  if (igreja?.id) {
    console.log('🖼️ Usando ícones locais da igreja:', igreja.id);
    const icon512Url = `${baseUrl}/512X512 - ${igreja.id}.png`;
    const icon192Url = `${baseUrl}/${igreja.id}.png`;
    console.log('🔗 URL do ícone 512x512:', icon512Url);
    console.log('🔗 URL do ícone 192x192:', icon192Url);
    
    icons = [
      {
        src: icon512Url,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any maskable'
      },
      {
        src: icon192Url,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any maskable'
      }
    ];
  } else {
    console.warn('⚠️ ID da igreja não encontrado, usando ícones padrão')
    // Ícones padrão apenas se não houver ID
    icons = [
      {
        src: `${baseUrl}/pwa-192x192.png`,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any maskable'
      },
      {
        src: `${baseUrl}/pwa-512x512.svg`,
        sizes: '512x512',
        type: 'image/svg+xml',
        purpose: 'any maskable'
      }
    ]
  }

  console.log('📱 Manifest gerado:', { name, shortName, icons })
  return {
    name,
    short_name: shortName,
    description,
    start_url: `${baseUrl}/`,
    display: 'standalone',
    background_color: backgroundColor,
    theme_color: themeColor,
    orientation: 'portrait-primary',
    icons,
    categories: ['productivity', 'utilities', 'lifestyle'],
    lang: 'pt-BR',
    scope: `${baseUrl}/`
  }
}

/**
 * Gera o HTML meta tags dinâmicas para PWA
 */
export function generatePWAMetaTags(igreja?: Igreja | null): string {
  const manifest = generatePWAManifest(igreja)
  
  return `
    <meta name="application-name" content="${manifest.name}">
    <meta name="apple-mobile-web-app-title" content="${manifest.short_name}">
    <meta name="description" content="${manifest.description}">
    <meta name="theme-color" content="${manifest.theme_color}">
    <meta name="msapplication-TileColor" content="${manifest.theme_color}">
    <meta name="apple-mobile-web-app-capable" content="yes">
    <meta name="apple-mobile-web-app-status-bar-style" content="default">
    <meta name="mobile-web-app-capable" content="yes">
  `.trim()
}

/**
 * Atualiza o título da página dinamicamente
 */
export function updatePageTitle(igreja?: Igreja | null, pageTitle?: string): void {
  const churchName = igreja?.nome || 'IgrejaDigital'
  const title = pageTitle ? `${pageTitle} - ${churchName}` : churchName
  document.title = title
}

/**
 * Atualiza o favicon dinamicamente baseado no ID da igreja
 */
export function updateFavicon(igreja?: Igreja | null): void {
  // Definir URL do favicon baseado no ID da igreja
  let faviconUrl = '/vite.svg' // fallback padrão
  
  if (igreja?.id) {
    // Usar o ícone de 192x192px da igreja (mesmo padrão do PWA)
    faviconUrl = `/${igreja.id}.png`
    console.log('🎯 Favicon atualizado para igreja:', igreja.id, faviconUrl)
  } else {
    console.log('🎯 Usando favicon padrão (sem ID da igreja)')
  }
  
  // Atualizar ou criar link do favicon
  let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement
  if (faviconLink) {
    faviconLink.href = faviconUrl
    faviconLink.type = igreja?.id ? 'image/png' : 'image/svg+xml'
  } else {
    faviconLink = document.createElement('link')
    faviconLink.rel = 'icon'
    faviconLink.type = igreja?.id ? 'image/png' : 'image/svg+xml'
    faviconLink.href = faviconUrl
    document.head.appendChild(faviconLink)
  }
  
  // Também atualizar apple-touch-icon se houver ID da igreja
  if (igreja?.id) {
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement
    if (appleTouchIcon) {
      appleTouchIcon.href = faviconUrl
    } else {
      appleTouchIcon = document.createElement('link')
      appleTouchIcon.rel = 'apple-touch-icon'
      appleTouchIcon.href = faviconUrl
      document.head.appendChild(appleTouchIcon)
    }
  }
}

/**
 * Atualiza as meta tags dinamicamente no DOM
 */
export function updatePWAMetaTags(
  igreja?: Igreja | null, 
  pageData?: {
    title?: string
    description?: string
    image?: string
    type?: string
  }
): void {
  const manifest = generatePWAManifest(igreja)
  const ogData = generateOpenGraphData(igreja, pageData)
  
  // Atualizar favicon dinamicamente
  updateFavicon(igreja)
  
  // Atualizar theme-color
  updateMetaTag('theme-color', manifest.theme_color)
  
  // Atualizar description
  updateMetaTag('description', manifest.description)
  
  // Atualizar application-name
  updateMetaTag('application-name', manifest.name)
  
  // Atualizar Open Graph tags
  updateMetaProperty('og:title', ogData.title)
  updateMetaProperty('og:description', ogData.description)
  updateMetaProperty('og:image', ogData.image)
  updateMetaProperty('og:url', ogData.url)
  updateMetaProperty('og:type', ogData.type)
  updateMetaProperty('og:site_name', ogData.siteName)
  updateMetaProperty('og:locale', ogData.locale)
  
  // Atualizar Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image')
  updateMetaTag('twitter:title', ogData.title)
  updateMetaTag('twitter:description', ogData.description)
  updateMetaTag('twitter:image', ogData.image)
}

/**
 * Função auxiliar para atualizar meta tags com name
 */
function updateMetaTag(name: string, content: string): void {
  let metaTag = document.querySelector(`meta[name="${name}"]`)
  if (metaTag) {
    metaTag.setAttribute('content', content)
  } else {
    metaTag = document.createElement('meta')
    metaTag.setAttribute('name', name)
    metaTag.setAttribute('content', content)
    document.head.appendChild(metaTag)
  }
}

/**
 * Função auxiliar para atualizar meta tags com property
 */
function updateMetaProperty(property: string, content: string): void {
  let metaTag = document.querySelector(`meta[property="${property}"]`)
  if (metaTag) {
    metaTag.setAttribute('content', content)
  } else {
    metaTag = document.createElement('meta')
    metaTag.setAttribute('property', property)
    metaTag.setAttribute('content', content)
    document.head.appendChild(metaTag)
  }
}